import {
  OFFICE_365_DEFAULT_REDIRECT,
  OFFICE_365_PERMISSION_MODULE_NAME,
  OFFICE_365_PERMISSION_PATH,
} from '@my-backoffice/app/pages/login/federated/office365/constants/office365-auth.constants';
import { toOffice365AppRedirectPath } from '@my-backoffice/app/pages/login/federated/office365/functions/office365-redirect-url.functions';
import { SERVER_URL } from '@my-backoffice/app/shared/constants/app.constants';
import { EStorageKeys } from '@my-backoffice/app/shared/models/enums/storage-keys.enum';
import { IHttpResponseBody } from '@my-backoffice/app/shared/models/interfaces/http-response-body.interface';
import { HttpService } from '@my-backoffice/app/shared/services/http-services/http.service';
import { StorageService } from '@my-backoffice/app/shared/services/storage-services/storage.service';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

/**
 * Temporary fallback when auth2 `current-user` returns no module resources.
 * RBAC is not enforced on my-backoffice yet — anyone who can log in may use all features.
 */
const MY_BACKOFFICE_FALLBACK_PERMISSION_ALL = 'All';

@Injectable({
  providedIn: 'root',
})
export class Office365UserPermissionsService {
  public constructor(
    private readonly _httpService: HttpService,
    private readonly _storageService: StorageService,
    private readonly _cookieService: CookieService,
    private readonly _router: Router
  ) {}

  /**
   * Load permissions after login and redirect when successful.
   * @param {string | undefined} redirectUrl - Post-login redirect path.
   * @returns {void}
   */
  public saveUserPermissionsToStore(redirectUrl?: string): void {
    this._getUserPermissions$().subscribe({
      next: (userPermissions: string[]): void => {
        if (!userPermissions.length) {
          void this._router.navigate(['/unauthorized']);

          return;
        }

        this._storageService.setItem(EStorageKeys.USER_PERMISSIONS, JSON.stringify(userPermissions));
        this._cookieService.put(EStorageKeys.USER_PERMISSIONS, JSON.stringify(userPermissions));
        void this._router.navigateByUrl(toOffice365AppRedirectPath(redirectUrl || OFFICE_365_DEFAULT_REDIRECT, location.origin));
      },
      error: (): void => {
        void this._router.navigate(['/unauthorized']);
      },
    });
  }

  /**
   * @returns {Observable<string[]>} Current user permission keys.
   */
  public getUserPermissionsArray$(): Observable<string[]> {
    const stored = this._storageService.getItem(EStorageKeys.USER_PERMISSIONS);

    if (stored === '') {
      return this._getUserPermissions$().pipe(
        switchMap((permissions: string[]): Observable<string[]> => {
          if (!permissions.length) {
            return this._redirectNoAuthorized$();
          }

          this._storageService.setItem(EStorageKeys.USER_PERMISSIONS, JSON.stringify(permissions));

          return of(permissions);
        })
      );
    }

    if (stored === '[]') {
      return this._redirectNoAuthorized$();
    }

    return of(JSON.parse(stored || '[]') as string[]);
  }

  private _getUserPermissions$(): Observable<string[]> {
    const url = `${SERVER_URL}${OFFICE_365_PERMISSION_PATH}/${OFFICE_365_PERMISSION_MODULE_NAME}/users/current-user`;

    return this._httpService.httpClient.get<IHttpResponseBody<string[]>>(url, { observe: 'body' }).pipe(
      map((response: Readonly<IHttpResponseBody<string[]>>): string[] => {
        const permissions = response.content ?? [];

        if (!permissions.length) {
          return [MY_BACKOFFICE_FALLBACK_PERMISSION_ALL];
        }

        return permissions;
      })
    );
  }

  private _redirectNoAuthorized$(): Observable<string[]> {
    void this._router.navigate(['/unauthorized']);

    return of([]);
  }
}
